import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  FiUsers,
  FiInbox,
  FiFile,
  FiMail,
  FiArrowRight,
  FiSend,
  FiZap,
  FiClock,
  FiTrendingUp,
  FiRefreshCw,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUser } from "@/features/auth";
import { CardSkeleton } from "@/components/common";

type DashboardStats = {
  customers: { total: number; recent: number };
  leads: { total: number; new: number; contacted: number };
  documents: { total: number; pending: number };
  subscribers: { total: number; active: number };
};

type RecentLead = {
  id: string;
  name: string;
  email: string;
  loanType: string;
  status: string;
  createdAt: string;
};

const CRON_JOBS = [
  {
    name: "Scrape RBA Rates",
    endpoint: "/api/cron/scrape-rba-rates",
    utcHour: 0,
    utcMinute: 0,
    description: "Fetches latest RBA interest rates",
    color: "bg-blue-100 text-blue-600",
  },
  {
    name: "Process Automations",
    endpoint: "/api/cron/process-automations",
    utcHour: 14,
    utcMinute: 0,
    description: "Triggers email automations",
    color: "bg-amber-100 text-amber-600",
  },
  {
    name: "Send Scheduled Emails",
    endpoint: "/api/cron/send-scheduled-emails",
    utcHour: 21,
    utcMinute: 0,
    description: "Sends queued scheduled emails",
    color: "bg-green-100 text-green-600",
  },
];

function getNextRun(utcHour: number, utcMinute: number): Date {
  const now = new Date();
  const next = new Date(now);
  next.setUTCHours(utcHour, utcMinute, 0, 0);
  if (next <= now) next.setUTCDate(next.getUTCDate() + 1);
  return next;
}

function formatRelative(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  if (diffHours > 0) return `in ${diffHours}h ${diffMinutes}m`;
  return `in ${diffMinutes}m`;
}

export function AdminDashboardPage() {
  const user = useUser();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentLeads, setRecentLeads] = useState<RecentLead[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const cronJobs = useMemo(
    () =>
      CRON_JOBS.map((job) => ({
        ...job,
        nextRun: getNextRun(job.utcHour, job.utcMinute),
      })),
    []
  );

  useEffect(() => {
    async function fetchDashboard() {
      try {
        const response = await fetch("/api/admin/dashboard", {
          credentials: "include",
        });
        if (response.ok) {
          const data = await response.json() as { stats: DashboardStats; recentLeads: RecentLead[] };
          setStats(data.stats);
          setRecentLeads(data.recentLeads || []);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchDashboard();
  }, []);

  const statCards = [
    {
      title: "Customers",
      icon: FiUsers,
      value: stats?.customers.total || 0,
      subtitle: `${stats?.customers.recent || 0} this month`,
      color: "bg-blue-100 text-blue-600",
      link: "/admin/customers",
    },
    {
      title: "Leads",
      icon: FiInbox,
      value: stats?.leads.total || 0,
      subtitle: `${stats?.leads.new || 0} new`,
      color: "bg-amber-100 text-amber-600",
      link: "/admin/leads",
    },
    {
      title: "Documents",
      icon: FiFile,
      value: stats?.documents.total || 0,
      subtitle: `${stats?.documents.pending || 0} pending review`,
      color: "bg-green-100 text-green-600",
      link: "/admin/documents",
    },
    {
      title: "Subscribers",
      icon: FiMail,
      value: stats?.subscribers.total || 0,
      subtitle: `${stats?.subscribers.active || 0} active`,
      color: "bg-purple-100 text-purple-600",
      link: "/admin/subscribers",
    },
  ];

  const loanTypeLabels: Record<string, string> = {
    home_purchase: "Home Purchase",
    refinance: "Refinance",
    investment: "Investment",
    first_home: "First Home",
    business: "Business",
    construction: "Construction",
  };

  const statusConfig: Record<string, { label: string; color: string }> = {
    new: { label: "New", color: "bg-blue-100 text-blue-700" },
    contacted: { label: "Contacted", color: "bg-yellow-100 text-yellow-700" },
    qualified: { label: "Qualified", color: "bg-green-100 text-green-700" },
    converted: { label: "Converted", color: "bg-emerald-100 text-emerald-700" },
    closed: { label: "Closed", color: "bg-gray-100 text-gray-700" },
  };

  return (
    <div className="space-y-8">
      {/* Welcome section */}
      <div>
        <h1 className="font-serif text-3xl font-medium text-slate-900">
          Admin Dashboard
        </h1>
        <p className="text-muted-foreground mt-1">
          Welcome back, {user?.name?.split(" ")[0] || "Admin"}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className={`p-3 rounded-full ${stat.color}`}>
                  <stat.icon className="w-5 h-5" />
                </div>
                <Link
                  to={stat.link}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <FiArrowRight className="w-5 h-5" />
                </Link>
              </div>
              <div className="mt-4">
                <p className="text-3xl font-semibold">
                  {isLoading ? "-" : stat.value}
                </p>
                <p className="text-sm text-muted-foreground">{stat.title}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {stat.subtitle}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <Button asChild className="btn-pill">
              <Link to="/admin/customers">
                <FiUsers className="mr-2 h-4 w-4" />
                Add Customer
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/leads">
                <FiInbox className="mr-2 h-4 w-4" />
                View Leads
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/documents">
                <FiFile className="mr-2 h-4 w-4" />
                Review Documents
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/campaigns">
                <FiSend className="mr-2 h-4 w-4" />
                Email Campaigns
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/automations">
                <FiZap className="mr-2 h-4 w-4" />
                Automations
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/scheduled-emails">
                <FiClock className="mr-2 h-4 w-4" />
                Scheduled Emails
              </Link>
            </Button>
            <Button asChild variant="outline" className="btn-pill">
              <Link to="/admin/rba-data">
                <FiTrendingUp className="mr-2 h-4 w-4" />
                RBA Data
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cron Jobs */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Cron Jobs</CardTitle>
          <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">
            Cloudflare Worker
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {cronJobs.map((job) => (
              <div
                key={job.name}
                className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-full ${job.color}`}>
                    <FiRefreshCw className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium">{job.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {job.description}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-medium">
                    {job.nextRun.toLocaleTimeString("en-AU", {
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZone: "Australia/Sydney",
                    })}{" "}
                    AEST
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatRelative(job.nextRun)}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-4">
            Runs daily via Cloudflare Worker cron triggers. Logs available via{" "}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">
              wrangler tail
            </code>
          </p>
        </CardContent>
      </Card>

      {/* Recent Leads */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Leads</CardTitle>
          <Button asChild variant="ghost" size="sm">
            <Link to="/admin/leads">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : recentLeads.length === 0 ? (
            <div className="text-center py-8">
              <FiInbox className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No leads yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentLeads.map((lead) => (
                <div
                  key={lead.id}
                  className="flex items-center justify-between p-4 rounded-lg bg-muted/50"
                >
                  <div className="min-w-0">
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {lead.email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {loanTypeLabels[lead.loanType] || lead.loanType} &bull;{" "}
                      {new Date(lead.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <Badge
                    variant="secondary"
                    className={statusConfig[lead.status]?.color || ""}
                  >
                    {statusConfig[lead.status]?.label || lead.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
